/* eslint-disable no-unused-vars */
import { Box, ButtonBase, Icon, Tooltip, styled } from '@mui/material';
import useSettings from 'app/hooks/useSettings';
import React, { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { Paragraph, Span } from '../Typography';
import MatxVerticalNavExpansionPanel from './MatxVerticalNavExpansionPanel';
import useAuth from 'app/hooks/useAuth';

const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: '12px',
  marginTop: '20px',
  marginLeft: '15px',
  marginBottom: '10px',
  textTransform: 'uppercase',
  display: mode === 'compact' && 'none',
  color: theme.palette.text.secondary
}));

const MyProListLabel = styled(Paragraph)(({ mode }) => ({
  display: mode === 'compact' ? 'none' : 'flex',
  alignItems: 'center',
  gap: 8,
  margin: '22px 24px 10px',
  fontSize: '10.5px',
  fontWeight: 800,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.5)',
  '&::before': {
    content: '""',
    width: 14,
    height: 3,
    borderRadius: 3,
    background: 'linear-gradient(90deg, var(--mp-gold), var(--mp-warm))'
  }
}));

const ExtAndIntCommon = {
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '4px',
  height: 44,
  whiteSpace: 'pre',
  marginBottom: '8px',
  textDecoration: 'none',
  justifyContent: 'space-between',
  transition: 'all 150ms ease-in',
  '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
  '&.compactNavItem': {
    overflow: 'hidden',
    justifyContent: 'center !important'
  },
  '& .icon': {
    fontSize: '18px',
    paddingLeft: '16px',
    paddingRight: '16px',
    verticalAlign: 'middle'
  }
};

const MyProExtAndIntCommon = {
  position: 'relative',
  display: 'flex',
  overflow: 'hidden',
  alignItems: 'center',
  padding: '6px 10px',
  height: '48px',
  margin: '0px 14px 6px 14px',
  borderRadius: '16px',
  whiteSpace: 'pre',
  textDecoration: 'none',
  justifyContent: 'space-between',
  border: '1px solid transparent',
  transition: 'all 250ms cubic-bezier(0.25, 0.8, 0.25, 1)',
  '&:hover': {
    background: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    transform: 'translateX(4px)',
    '& .icon': { background: 'rgba(255,255,255,0.16)', transform: 'scale(1.06) rotate(-6deg)' }
  },
  '&.compactNavItem': {
    overflow: 'hidden',
    justifyContent: 'center !important',
    margin: '0px 10px 6px 10px'
  },
  // Ikon dalam ubin kaca kecil
  '& .icon': {
    flexShrink: 0,
    width: '34px !important',
    height: 34,
    lineHeight: '34px',
    textAlign: 'center',
    fontSize: '19px',
    borderRadius: '11px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.1)',
    transition: 'all 250ms ease'
  }
};

const ExternalLink = styled('a')(() => ({
  ...MyProExtAndIntCommon,
  color: 'rgba(255,255,255,0.72)'
}));

const InternalLink = styled(Box)(({ theme }) => ({
  '& a': {
    ...ExtAndIntCommon,
    color: theme.palette.text.primary
  },
  '& .navItemActive': {
    backgroundColor: 'rgba(255, 255, 255, 0.16)'
  }
}));

const MyProInternalLink = styled(Box)(() => ({
  '& a': {
    ...MyProExtAndIntCommon,
    color: 'rgba(255,255,255,0.72)',
    fontWeight: 500
  },
  // Menu aktif: pill kaca terang + garis aksen kuning-oranye + ikon bergradasi
  '& .navItemActive': {
    color: '#FFF',
    background: 'linear-gradient(90deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))',
    borderColor: 'rgba(255,255,255,0.28)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 10px 24px -12px rgba(0,0,0,0.6)',
    backdropFilter: 'blur(10px)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 10,
      bottom: 10,
      width: 4,
      borderRadius: '0 4px 4px 0',
      background: 'linear-gradient(180deg, var(--mp-gold), var(--mp-warm))'
    },
    '& .menu-text': { fontWeight: 800 },
    '&:hover': {
      background: 'linear-gradient(90deg, rgba(255,255,255,0.26), rgba(255,255,255,0.1))',
      color: '#FFF',
      transform: 'translateX(4px)'
    },
    '& .icon, &:hover .icon': {
      color: 'var(--mp-brand)',
      border: 'none',
      transform: 'none',
      background: 'linear-gradient(135deg, var(--mp-gold), var(--mp-warm))',
      boxShadow: '0 6px 14px -4px rgba(var(--mp-warm-rgb), 0.7)'
    }
  }
}));

const StyledText = styled(Span)(({ mode }) => ({
  fontSize: '0.875rem',
  paddingLeft: '0.8rem',
  display: mode === 'compact' && 'none'
}));

const MyProStyledText = styled(Span)(({ mode }) => ({
  fontSize: '14px',
  paddingLeft: '0.75rem',
  display: mode === 'compact' && 'none',
  fontFamily: 'inherit',
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: '0.01em',
  lineHeight: 'normal'
}));

const BulletIcon = styled('div')(({ theme }) => ({
  padding: '2px',
  marginLeft: '24px',
  marginRight: '8px',
  overflow: 'hidden',
  borderRadius: '300px',
  background: theme.palette.text.primary
}));

const BadgeValue = styled('div')(() => ({
  padding: '1px 8px',
  overflow: 'hidden',
  borderRadius: '300px'
}));

// Font yang dimuat hanya "Material Icons" (filled) — nama berakhiran
// "_outlined" (mis. article_outlined) tidak dikenali font itu, sisa teksnya
// mendorong ikon keluar dari tengah. Pakai nama dasarnya saja.
const iconName = (icon) => String(icon).replace(/_outlined$/, '');

const MatxVerticalNav = ({ items }) => {
  const { settings } = useSettings();
  const {user} = useAuth()
  const { mode } = settings.layout1Settings.leftSidebar;

  const renderLevels = (data) => {
    return data.filter(item => item.roles.includes(user?.role)).map((item, index) => {
      if (item.type === 'label')
        return (

          <MyProListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </MyProListLabel>

        );

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel mode={mode} item={item} key={index}>
            {renderLevels(item.children)}
          </MatxVerticalNavExpansionPanel>
        );
      } else if (item.type === 'extLink') {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            className={`${mode === 'compact' && 'compactNavItem'}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ButtonBase key={item.name} name="child" sx={{ width: '100%' }}>
              {(() => {
                if (item.icon) {
                  return <Icon className="icon">{iconName(item.icon)}</Icon>;
                } else {
                  return <span className="item-icon icon-text">{item.iconText}</span>;
                }
              })()}
              <MyProStyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </MyProStyledText>
              <Box mx="auto"></Box>
              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else {
        return (

          <MyProInternalLink key={index}>
            <Tooltip title={mode === 'compact' ? item.name : ''} placement="right" arrow disableInteractive>
            <Box>
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                isActive
                  ? `navItemActive ${mode === 'compact' && 'compactNavItem'}`
                  : `${mode === 'compact' && 'compactNavItem'}`
              }
            >
              <ButtonBase key={item.name} name="child" sx={{ width: '100%' }}>
                {item?.icon ? (
                  <Icon className="icon">
                    {iconName(item.icon)}
                  </Icon>
                ) : (
                  <Fragment>
                    <BulletIcon
                      className={`nav-bullet`}
                      sx={{ display: mode === 'compact' && 'none' }}
                    />
                    <Box
                      className="nav-bullet-text"
                      sx={{
                        ml: '20px',
                        fontSize: '11px',
                        display: mode !== 'compact' && 'none'
                      }}
                    >
                      {item.iconText}
                    </Box>
                  </Fragment>
                )}
                <MyProStyledText mode={mode} className="sidenavHoverShow menu-text">
                  {item.name}
                </MyProStyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">{item.badge.value}</BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
            </Box>
            </Tooltip>
          </MyProInternalLink>

        );
      }
    });
  };

  return <div className="navigation">{renderLevels(items)}</div>;
};

export default React.memo(MatxVerticalNav);
